import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useTheme } from '../contexts/ThemeContext';
import { hasOnboardingAccess } from '../services/onboardingService';
import BBALoader from './BBALoader';

const OnboardingWrapper = ({ children, currentStep }) => {
  const { currentUser } = useAuth();
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [accountType, setAccountType] = useState(null);
  const isDev = process.env.NODE_ENV === 'development';

  useEffect(() => {
    const fetchOnboardingStatus = async () => {
      if (currentUser) {
        try {
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            const data = userDoc.data();
            setOnboardingStatus(data.onboardingStatus);
            
            // If this is a portal user with a linked account, check the account type
            if (data.accountId) {
              try {
                const accountDoc = await getDoc(doc(db, 'accounts', data.accountId));
                if (accountDoc.exists()) {
                  const accountData = accountDoc.data();
                  setAccountType(accountData.type);
                }
              } catch (error) {
                console.error('Error fetching linked account:', error);
              }
            }
            
            // For new Portal users (status: 'onboarding'), automatically grant access
            if (data.status === 'onboarding' && data.onboardingStatus) {
              setHasAccess(true);
            } else {
              // For existing users, check if they have onboarding key access
              const access = await hasOnboardingAccess(currentUser.uid);
              setHasAccess(access);
            }
          }
        } catch (error) {
          console.error('Error fetching onboarding status:', error);
        }
      }
      setLoading(false);
    };

    fetchOnboardingStatus();
  }, [currentUser]);

  const updateOnboardingStep = async (step, completed = false) => {
    if (!currentUser || !onboardingStatus) return;

    try {
      const updatedStatus = {
        ...onboardingStatus,
        currentStep: step,
        steps: {
          ...onboardingStatus.steps,
          [currentStep]: {
            ...onboardingStatus.steps[currentStep],
            completed: completed
          }
        }
      };

      // Check if all required steps are completed
      const allRequiredCompleted = Object.entries(updatedStatus.steps)
        .filter(([key, step]) => step.required)
        .every(([key, step]) => step.completed);

      if (allRequiredCompleted) {
        updatedStatus.completed = true;
      }

      // Update the user document
      await updateDoc(doc(db, 'users', currentUser.uid), {
        onboardingStatus: updatedStatus,
        status: allRequiredCompleted ? 'active' : 'onboarding'
      });

      setOnboardingStatus(updatedStatus);

      // If onboarding is complete, redirect based on account type
      if (allRequiredCompleted) {
        // Check account type to determine redirect
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          if (userData.accountId) {
            try {
              const accountDoc = await getDoc(doc(db, 'accounts', userData.accountId));
              if (accountDoc.exists()) {
                const accountData = accountDoc.data();
                if (accountData.type === 'Clients' || accountData.type === 'clients') {
                  // Client account - redirect to portal
                  navigate('/portal', { 
                    state: { 
                      message: 'Onboarding completed successfully! Welcome to your portal dashboard.' 
                    }
                  });
                } else {
                  // Lead account - redirect to start
                  navigate('/start', { 
                    state: { 
                      message: 'Onboarding completed successfully!' 
                    }
                  });
                }
              } else {
                // No account found - redirect to start
                navigate('/start');
              }
            } catch (error) {
              console.error('Error fetching account for redirect:', error);
              // Default to start on error
              navigate('/start');
            }
          } else {
            // No account ID - redirect to start
            navigate('/start');
          }
        } else {
          // User doc doesn't exist - redirect to start
          navigate('/start');
        }
      }
    } catch (error) {
      console.error('Error updating onboarding status:', error);
    }
  };

  const startPathForStep = (step) => {
    if (step === 'profile-completion') return '/start/profile';
    if (step === 'existing-conditions') return '/start/existing-conditions';
    return `/start/${step}`;
  };

  const goToNextStep = () => {
    const steps = ['existing-conditions', 'nda', 'profile-completion'];
    const currentIndex = steps.indexOf(currentStep);
    
    if (currentIndex < steps.length - 1) {
      const nextStep = steps[currentIndex + 1];
      updateOnboardingStep(nextStep);
      navigate(startPathForStep(nextStep));
    } else {
      updateOnboardingStep(currentStep, true);
    }
  };

  const goToPreviousStep = () => {
    const steps = ['existing-conditions', 'nda', 'profile-completion'];
    const currentIndex = steps.indexOf(currentStep);
    
    if (currentIndex > 0) {
      const previousStep = steps[currentIndex - 1];
      navigate(startPathForStep(previousStep));
    }
  };

  if (loading && !isDev) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <BBALoader size={128} />
      </div>
    );
  }

  // In dev mode, create a mock onboarding status if none exists
  if (isDev && !onboardingStatus) {
    const mockOnboardingStatus = {
      completed: false,
      currentStep: currentStep,
      steps: {
        'existing-conditions': {
          completed: false,
          required: true
        },
        'nda': {
          completed: false,
          required: true
        },
        'profile-completion': {
          completed: false,
          required: true
        }
      }
    };
    setOnboardingStatus(mockOnboardingStatus);
  }

  // If user is not in onboarding mode and not in dev mode, redirect them
  if (!onboardingStatus || onboardingStatus.completed) {
    if (!isDev) {
      // Check account type to determine redirect
      if (accountType === 'Clients' || accountType === 'clients') {
        navigate('/portal');
      } else {
        navigate('/start');
      }
      return null;
    }
  }

  // Check if user has access to onboarding walkthrough
  if (!hasAccess && !isDev) {
    // For new Portal users who just signed up, they should have access
    // Let's check if this is a timing issue and give them access
    if (onboardingStatus && onboardingStatus.currentStep) {
      setHasAccess(true);
    } else {
      return (
        <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gray-50'} flex items-center justify-center`}>
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-lg p-8 max-w-md w-full mx-4`}>
            <div className="text-center">
              <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h2 className={`text-2xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Access Required
              </h2>
              <p className={`mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                You need an Onboarding Key to access the onboarding walkthrough. This is only required for existing users who want to re-access the onboarding process. New Portal users automatically have access to onboarding.
              </p>
              <button
                onClick={() => {
                  // Check account type to determine redirect
                  if (accountType === 'Clients' || accountType === 'clients') {
                    navigate('/portal');
                  } else {
                    navigate('/start');
                  }
                }}
                className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                  darkMode 
                    ? 'bg-red-600 hover:bg-red-700 text-white' 
                    : 'bg-red-600 hover:bg-red-700 text-white'
                }`}
              >
                Return to Dashboard
              </button>
            </div>
          </div>
        </div>
      );
    }
  }

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-white'}`}>
      {/* Onboarding Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Step Title - Hide for NDA step to prevent duplication */}
        {currentStep !== 'nda' && currentStep !== 'profile-completion' && (
          <div className="mb-8 text-center">
            <h1 className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {currentStep === 'existing-conditions' && 'Business Assessment'}
            </h1>
            <p className={`mt-2 text-lg ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {currentStep === 'existing-conditions' && 'Let\'s understand your current business state to create a personalized roadmap for your success'}
            </p>
          </div>
        )}

        {/* Render the child component with onboarding context */}
        {React.cloneElement(children, {
          onboardingStatus,
          updateOnboardingStep,
          goToNextStep,
          goToPreviousStep,
          currentStep
        })}
      </div>
    </div>
  );
};

export default OnboardingWrapper; 